// SWR Keys
// These keys identify cached data for SWR and are used to trigger revalidation.
// Keys can be used as-is or modified with suffixes or prefixes to represent
// paginated data, filtered views, or other variations.
// For example, "auth/upcoming-orders-page-index-0" represents page 0 of upcoming orders.
//
// Keys are split into two enums by security scope:
// - PUBLIC_SWR_KEYS = Available without authentication, persist across login/logout
// - AUTH_SWR_KEYS = Requires authentication, cleared on logout
/**
 * Public data keys that don't require authentication.
 * These should remain cached across login/logout cycles.
 */
export var PUBLIC_SWR_KEYS;
(function (PUBLIC_SWR_KEYS) {
    PUBLIC_SWR_KEYS["COMPANY_PROVIDER"] = "public/company-provider";
    PUBLIC_SWR_KEYS["DELIVERY_QUESTIONS"] = "public/delivery-questions";
    PUBLIC_SWR_KEYS["COMPANY_ADDRESS"] = "public/company-address";
    PUBLIC_SWR_KEYS["COMPANY_GEOLOCATION"] = "public/company-geolocation";
    PUBLIC_SWR_KEYS["PLANS"] = "public/plans";
    PUBLIC_SWR_KEYS["GENERAL_SETTINGS"] = "public/general-settings";
    PUBLIC_SWR_KEYS["ACCOUNT_SETTINGS"] = "public/account-settings";
    PUBLIC_SWR_KEYS["REGISTER_FORM_DATA"] = "public/register-form-data";
})(PUBLIC_SWR_KEYS || (PUBLIC_SWR_KEYS = {}));
/**
 * 🚨 SECURITY BOUNDARY 🚨
 *
 * Authenticated data keys that require user authentication.
 *
 * CRITICAL: All user-specific data MUST use keys from AUTH_SWR_KEYS enum
 * to ensure proper clearing on logout and prevent data leakage across sessions.
 *
 * When a user logs out, all keys in this enum (and their suffixed variations)
 * are automatically cleared from the cache.
 *
 * Guidelines for adding new keys:
 * 1. If the data is user-specific or requires authentication → Add to AUTH_SWR_KEYS enum
 * 2. All keys MUST use the "auth/" prefix
 * 3. Test that the data is properly cleared on logout
 * 4. Document any suffixed variations (pagination, filters) in comments
 *
 * Failure to properly categorize authenticated keys can result in:
 * - Sensitive data persisting after logout
 * - User data visible to subsequent users on shared devices
 * - Security vulnerabilities and compliance violations
 */
export var AUTH_SWR_KEYS;
(function (AUTH_SWR_KEYS) {
    AUTH_SWR_KEYS["SKIP_DAYS"] = "auth/skip-days";
    AUTH_SWR_KEYS["UPCOMING_ORDERS"] = "auth/upcoming-orders";
    AUTH_SWR_KEYS["ORDERS_BY_DATE"] = "auth/orders-by-date";
    AUTH_SWR_KEYS["MY_MEALS_MENU"] = "auth/my-meals-menu";
    AUTH_SWR_KEYS["CUSTOMER_INFO"] = "auth/customer-info";
    AUTH_SWR_KEYS["DELIVERY_ADDRESSES"] = "auth/delivery-addresses";
    AUTH_SWR_KEYS["PAYMENT_METHODS"] = "auth/payment-methods";
    AUTH_SWR_KEYS["CART_SETTINGS"] = "auth/cart-settings";
})(AUTH_SWR_KEYS || (AUTH_SWR_KEYS = {}));
/**
 * Utility function to clear all authenticated data from SWR cache.
 * Uses predicate-based clearing to handle suffixed keys (pagination, filters, etc.).
 *
 * Example: If base key is "auth/upcoming-orders", this will also clear:
 * - "auth/upcoming-orders-page-index-0"
 * - "auth/upcoming-orders-page-index-1"
 * - "auth/upcoming-orders-filter-xyz"
 *
 * @param mutate - The mutate function from useSWRConfig()
 */
export const clearAuthenticatedCache = (mutate) => {
    // Get all authenticated key values from the AUTH_SWR_KEYS enum
    const authKeys = Object.values(AUTH_SWR_KEYS);
    authKeys.forEach((baseKey) => {
        // Clear all cache entries whose keys start with this authenticated base key
        mutate((key) => typeof key === "string" && key.startsWith(baseKey), undefined, { revalidate: false });
    });
};
